// src/components/MedicalCoding.js
import React, { useState } from 'react';
import './MedicalCoding.css';

const MedicalCoding = () => {
  const [formData, setFormData] = useState({
    service: '',
    quote: '',
    fullName: '',
    phone: '',
    email: '',
    website: '',
    reachVia: '',
    preferredTime: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const serviceOptions = [
    'Medical Coding', 'Medical Billing', 'Credentialing', 
    'Denial Management', 'AR Follow-up', 'Revenue Cycle Management'
  ];

  const quoteOptions = ['Standard', 'Premium', 'Enterprise', 'Custom'];
  const reachViaOptions = ['Email', 'Phone', 'Both'];
  const timeOptions = ['Morning (9AM - 12PM)', 'Afternoon (12PM - 4PM)', 'Evening (4PM - 7PM)', 'Anytime'];

  const specialties = [
    'Ophthalmology', 'Geriatrics', 'Rehab', 'Neurosurgery',
    'Nephrology', 'Podiatry', 'Pediatric', 'Dermatology'
  ];

  const codingServices = [
    { title: 'CPT & HCPCS Procedure Coding', desc: 'We accurately code patient diagnoses based on provider documentation to meet payer and CMS requirements.' },
    { title: 'ICD-10 Diagnosis Coding', desc: 'We accurately code patient diagnoses based on provider documentation to meet payer and CMS requirements.' },
    { title: 'Modifier Coding', desc: 'We apply appropriate CPT and HCPCS modifiers based on payer-specific rules to prevent denials and underpayments.' },
    { title: 'Coding Review & Quality Audits', desc: 'We perform regular coding audits to identify errors, undercoding, overcoding, and compliance risks.' },
    { title: 'Provider Documentation Support', desc: 'We help providers improve documentation to support accurate coding and compliance.' },
    { title: 'Denial Prevention Through Accurate Coding', desc: 'Many claim denials occur due to coding errors, but proactive coding approaches reduce claims.' }
  ];

  const whyChooseFeatures = [
    { icon: '🔗', title: 'Seamless Billing Integration', desc: 'Our coding services integrate seamlessly with our revenue-leading web tools.' },
    { icon: '🎯', title: 'Specialty-Focused Coding', desc: 'Push speed in handling billing codes, reduce risk of quality issues, and improve efficiency.' },
    { icon: '🔒', title: 'HIPAA-Compliant & Secure', desc: 'We follow strict data security and HIPAA compliance protocols.' },
    { icon: '👨‍💻', title: 'Certified & Experienced Coders', desc: 'Our coding service is 100% U.S.-based, certified in various coding standards and payer guidelines.' },
    { icon: '📈', title: 'Improved Revenue & Compliance', desc: 'Increase coding leads, increase revenue, and improve compliance.' }
  ];

  const processSteps = [
    'Ensure code to clinical documentation',
    'Detailed documentation review',
    'Accurate code assignment',
    'Quality checks & compliance review',
    'Submission to billing team',
    'Ongoing monitoring & optimization'
  ];

  const complianceStandards = [
    'ICD-10-CM guidelines',
    'CPT & HCPCS coding rules',
    'CMS regulations',
    'Payor-specific coding policies',
    'HIPAA privacy & security standards'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you! We will contact you shortly.' });
        setFormData({ service: '', quote: '', fullName: '', phone: '', email: '', website: '', reachVia: '', preferredTime: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus({ type: '', message: '' }), 5000);
    }
  };

  return (
    <main className="medical-coding-page">
      {/* Hero Section */}
      <section className="coding-hero">
        <div className="container">
          <div className="coding-hero-content">
            <h1 className="coding-hero-title">
              Customized Medical Coding Services <span className="highlight">in the USA</span>
            </h1>
            <p className="coding-hero-description">
              Our AHIMA & AAPC Certified Medical Coding Experts deliver accurate, compliant documentation tailored to your practice, 
              helping reduce denials, improve reimbursement, and support efficient revenue cycle management. We support healthcare 
              providers across the United States with HIPAA-compliant, specialty-specific medical coding solutions.
            </p>
            <div className="coding-hero-badges">
              <div className="hero-badge">Report Medical Coding for Maximum Revenue</div>
              <div className="hero-badge">100% Accurate & HIPAA-Compliant Coding Solutions</div>
            </div>
            <button className="hero-appoint-btn">Schedule An Appointment</button>
          </div>
        </div>
      </section>

      {/* Consultation Form Section */}
      <section className="coding-consultation">
        <div className="container">
          <div className="coding-form-wrapper">
            <h2 className="coding-form-title">Get a free Consultation</h2>
            {submitStatus.message && <div className={`status-message ${submitStatus.type}`}>{submitStatus.message}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <select name="service" value={formData.service} onChange={handleChange} required>
                    <option value="">Select Service</option>
                    {serviceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <select name="quote" value={formData.quote} onChange={handleChange} required>
                    <option value="">Select Quote</option>
                    {quoteOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleChange} required /></div>
                <div className="form-group"><input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required /></div>
                <div className="form-group"><input type="url" name="website" placeholder="Website/Appointment" value={formData.website} onChange={handleChange} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <select name="reachVia" value={formData.reachVia} onChange={handleChange} required>
                    <option value="">Reach Out Via</option>
                    {reachViaOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <select name="preferredTime" value={formData.preferredTime} onChange={handleChange} required>
                    <option value="">Preferred Time</option>
                    {timeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" className="coding-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Get Started'}</button>
            </form>
          </div>
        </div>
      </section>

      {/* Our Medical Coding Services Section */}
      <section className="our-coding-services">
        <div className="container">
          <div className="coding-services-header">
            <h2 className="section-title-dark">Our Medical Coding Services</h2>
            <p className="section-subtitle-dark">At the heart of our medical coding services is accuracy and attention to detail. We make sure your records are correct, helping your practice run smoothly and get the reimbursement it deserves.</p>
          </div>
          <div className="coding-services-grid">
            {codingServices.map((service, index) => (
              <div className="coding-service-card" key={index}>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty Based Section */}
      <section className="coding-specialty">
        <div className="container">
          <div className="coding-specialty-content">
            <h2 className="coding-specialty-title">Specialty Based Medical Coding Services</h2>
            <p className="coding-specialty-text">
              Providers Care Billing LLC works with all specialties and we understand that each specialty requires in-depth knowledge 
              of unique coding guidelines, payer policies, and documentation standards. Our AHIMA & AAPC certified medical coders 
              provide accurate, compliant, and efficient coding tailored to each practice, helping reduce denials, ensure proper 
              reimbursement, and support a smooth revenue cycle.
            </p>
            <div className="specialty-tags">
              {specialties.map(spec => <span key={spec} className="specialty-tag">{spec}</span>)}
            </div>
            <button className="view-specialties-btn">View All Specialties →</button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="coding-why-choose">
        <div className="container">
          <h2 className="why-choose-coding-title">Why Choose Our Medical Coding Services?</h2>
          <p className="why-choose-subtitle">Choosing the right medical coding company can make all the difference for your practice. Our top medical coding services focus on accuracy, compliance, and speeding up your revenue cycle so you can focus on patient care.</p>
          <div className="why-choose-coding-grid">
            {whyChooseFeatures.map((feature, index) => (
              <div className="coding-why-card" key={index}>
                <div className="coding-why-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="coding-process">
        <div className="container">
          <h2 className="coding-process-title">Our Medical Coding Process</h2>
          <div className="coding-process-steps">
            {processSteps.map((step, index) => (
              <div className="coding-process-step" key={index}>
                <span className="step-number">{index + 1}</span>
                <span className="step-text">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="coding-compliance">
        <div className="container">
          <div className="compliance-wrapper">
            <h2 className="compliance-title">Coding Standards & Compliance</h2>
            <p className="compliance-text">We strictly adhere to:</p>
            <div className="compliance-list">
              {complianceStandards.map((standard, index) => (
                <div className="compliance-item" key={index}>✓ {standard}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Services Section */}
      <section className="coding-trusted">
        <div className="container">
          <div className="coding-trusted-content">
            <h2>Trusted Medical Coding Services in the USA</h2>
            <p>Whether you are a solo provider, group practice, or multi-location clinic, Providers Care Billing LLC delivers scalable, accurate, and compliant medical coding services tailored to your specialty and practice needs.</p>
            <button className="coding-trusted-btn">Schedule a Free Medical Coding Consultation Today</button>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="coding-badges">
        <div className="container">
          <div className="badges-wrapper">
            {/* <div className="badge-item">4.8 ⭐⭐⭐⭐⭐</div> */}
            <div className="badge-item">ACCREDITED BUSINESS</div>
            <div className="badge-item">HIPAA COMPLIANT</div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MedicalCoding;